import { useLocation, useNavigate } from "react-router-dom";
import { X, Tag, ChevronDown, ChevronRight, Bot, Package, ShoppingBag, Headphones, Activity, Columns3, LayoutDashboard, LogIn, ArrowUpRight, Sparkles } from "lucide-react";
import UserAvatar from "@/components/UserAvatar";
import { useEffect, useRef, useState } from "react";
import { API } from "@/App";
import axios from "axios";
import { useAuth } from "@/App";
import { useLanguage } from "@/lib/i18n";

const DiscordIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189z"/>
  </svg>
);

const PAYMENT_LOGOS = [
  { name: "Visa", svg: <svg viewBox="0 0 48 32" className="h-6 w-auto"><rect width="48" height="32" rx="4" fill="#1A1F71"/><text x="24" y="20" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold" fontFamily="sans-serif">VISA</text></svg> },
  { name: "Mastercard", svg: <svg viewBox="0 0 48 32" className="h-6 w-auto"><rect width="48" height="32" rx="4" fill="#1a1a1a"/><circle cx="19" cy="16" r="8" fill="#EB001B"/><circle cx="29" cy="16" r="8" fill="#F79E1B"/><path d="M24 10.5a8 8 0 010 11" fill="#FF5F00"/></svg> },
  { name: "PayPal", svg: <svg viewBox="0 0 48 32" className="h-6 w-auto"><rect width="48" height="32" rx="4" fill="#003087"/><text x="24" y="20" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold" fontFamily="sans-serif">PayPal</text></svg> },
  { name: "Apple Pay", svg: <svg viewBox="0 0 48 32" className="h-6 w-auto"><rect width="48" height="32" rx="4" fill="#000"/><text x="24" y="20" textAnchor="middle" fill="white" fontSize="9" fontWeight="bold" fontFamily="sans-serif">Apple Pay</text></svg> },
  { name: "Google Pay", svg: <svg viewBox="0 0 48 32" className="h-6 w-auto"><rect width="48" height="32" rx="4" fill="#fff" stroke="#ddd"/><text x="24" y="20" textAnchor="middle" fill="#333" fontSize="8" fontWeight="bold" fontFamily="sans-serif">G Pay</text></svg> },
  { name: "CB", svg: <svg viewBox="0 0 48 32" className="h-6 w-auto"><rect width="48" height="32" rx="4" fill="#00843D"/><text x="24" y="20" textAnchor="middle" fill="white" fontSize="13" fontWeight="bold" fontFamily="sans-serif">CB</text></svg> },
];

function NavItem({ icon: Icon, label, desc, tint, active, onClick, delay, testId, trailing }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`sb-item group relative w-full flex items-center gap-3 rounded-xl px-2.5 py-2 text-left transition-colors ${active ? "bg-white/[0.07]" : "hover:bg-white/[0.04]"}`}
      style={{ animationDelay: `${delay}ms` }}
    >
      {active && <span className="absolute left-0 top-1/2 h-6 w-[3px] -translate-y-1/2 rounded-r-full bg-gradient-to-b from-blue-400 to-violet-500" />}
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] ring-1 ring-inset ring-white/[0.06] transition-transform duration-200 group-hover:scale-105 ${tint}`}>
        <Icon className="h-[18px] w-[18px]" />
      </span>
      <span className="min-w-0 flex-1">
        <span className={`block text-[13.5px] font-medium ${active ? "text-white" : "text-white/80 group-hover:text-white"}`}>{label}</span>
        {desc && <span className="block truncate text-[11.5px] text-white/35">{desc}</span>}
      </span>
      {trailing ?? <ChevronRight className="h-4 w-4 shrink-0 text-white/15 transition-all group-hover:translate-x-0.5 group-hover:text-white/40" />}
    </button>
  );
}

export default function Sidebar({ open, onClose, onAuthClick }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile } = useAuth();
  const { t } = useLanguage();
  const [shopBots, setShopBots] = useState([]);
  const [siteStatus, setSiteStatus] = useState(null);
  const [expandedTarif, setExpandedTarif] = useState(false);
  const [openBot, setOpenBot] = useState(null);
  const [visible, setVisible] = useState(open);
  const [closing, setClosing] = useState(false);

  const handleProtectedAction = (action) => {
    if (user) {
      action();
    } else {
      onClose();
      window.setTimeout(onAuthClick, 250);
    }
  };

  const go = (path) => { navigate(path); onClose(); };

  useEffect(() => {
    if (open) {
      setVisible(true);
      setClosing(false);
      return;
    }

    setClosing(true);
    const timeoutId = setTimeout(() => {
      setVisible(false);
      setClosing(false);
    }, 280);

    return () => clearTimeout(timeoutId);
  }, [open]);

  // Boutique et statut : chargés après l'animation d'ouverture (pas de nouveau
  // rendu pendant qu'elle glisse), puis gardés une minute entre deux ouvertures.
  const fetchedAt = useRef(0);
  useEffect(() => {
    if (!open || Date.now() - fetchedAt.current < 60000) return undefined;
    const id = window.setTimeout(() => {
      fetchedAt.current = Date.now();
      axios.get(`${API}/shop/bots`).then(r => setShopBots(r.data || [])).catch(() => {});
      axios.get(`${API}/site-status`).then(r => setSiteStatus(r.data)).catch(() => {});
    }, 400);
    return () => window.clearTimeout(id);
  }, [open]);

  // Échap ferme le menu.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const status = !siteStatus
    ? null
    : siteStatus.maintenance_mode || siteStatus.bots?.some(b => b.status === "outage")
    ? { dot: "bg-red-500", text: "text-red-300", bg: "bg-red-500/10", label: t('sidebar_status_down') }
    : siteStatus.bots?.some(b => b.status === "maintenance")
    ? { dot: "bg-amber-400", text: "text-amber-300", bg: "bg-amber-500/10", label: t('sidebar_status_warn') }
    : { dot: "bg-emerald-400", text: "text-emerald-300", bg: "bg-emerald-500/10", label: t('sidebar_status_ok') };

  if (!visible) return null;

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(`${path}/`);

  return (
    <>
      <div className={`fixed inset-0 z-[100] bg-black/65 ${closing ? "sb-overlay-out" : "sb-overlay-in"}`} onClick={onClose} />

      <aside
        className={`fixed left-0 top-0 bottom-0 z-[101] flex w-[300px] max-w-[86vw] flex-col overflow-hidden border-r border-white/[0.06] bg-[#0b0c14] shadow-[24px_0_60px_-20px_rgba(0,0,0,0.8)] sm:left-3 sm:top-3 sm:bottom-3 sm:rounded-2xl sm:border ${closing ? "sb-panel-out" : "sb-panel-in"}`}
        data-testid="sidebar"
      >
        <div className="pointer-events-none absolute -left-16 -top-20 h-64 w-80 bg-[radial-gradient(closest-side,rgba(37,99,235,0.22),rgba(124,58,237,0.1)_55%,transparent)]" />

        {/* En-tête */}
        <div className="relative flex items-center justify-between px-4 pb-3 pt-4">
          <button onClick={() => go("/")} className="flex items-center gap-2.5 text-left">
            <img src="/images/logo-navbar.png" alt="" className="h-9 w-9 rounded-full ring-1 ring-white/10" />
            <span className="leading-tight">
              <span className="block font-heading text-[15px] font-bold tracking-tight">VantaPulse</span>
              <span className="block text-[11px] text-white/40">{t('sidebar_tagline')}</span>
            </span>
          </button>
          <button onClick={onClose} className="rounded-lg p-2 text-white/40 transition hover:bg-white/[0.06] hover:text-white" aria-label="Fermer le menu">
            <X className="h-[18px] w-[18px]" />
          </button>
        </div>

        {/* Compte */}
        <div className="sb-item relative px-3 pb-2" style={{ animationDelay: "40ms" }}>
          {user ? (
            <button onClick={() => go("/dashboard")} className="group flex w-full items-center gap-3 rounded-xl border border-white/[0.06] bg-gradient-to-br from-white/[0.05] to-white/[0.015] p-3 text-left transition hover:border-blue-400/25">
              <UserAvatar profile={profile || user} size={38} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13.5px] font-semibold text-white">{profile?.username || user.username}</span>
                <span className="flex items-center gap-1 text-[11.5px] text-blue-300/90"><LayoutDashboard className="h-3 w-3" />{t('sidebar_open_dashboard')}</span>
              </span>
              <ChevronRight className="h-4 w-4 text-white/25 transition group-hover:translate-x-0.5 group-hover:text-white/60" />
            </button>
          ) : (
            <div className="rounded-xl border border-white/[0.06] bg-gradient-to-br from-blue-600/[0.12] to-violet-600/[0.08] p-3.5">
              <p className="text-[13.5px] font-semibold text-white">{t('sidebar_guest_title')}</p>
              <p className="mt-0.5 text-[12px] leading-snug text-white/50">{t('sidebar_guest_text')}</p>
              <button onClick={() => { onClose(); window.setTimeout(onAuthClick, 250); }} data-testid="sidebar-login" className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-violet-600 py-2 text-[13px] font-medium text-white shadow-lg shadow-blue-950/40 transition hover:brightness-110 active:scale-[0.98]">
                <LogIn className="h-4 w-4" />{t('sidebar_guest_cta')}
              </button>
            </div>
          )}
        </div>

        {/* Liens */}
        <nav className="relative flex-1 space-y-0.5 overflow-y-auto px-3 py-2">
          <p className="sb-item px-2.5 pb-1.5 pt-2 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-white/30" style={{ animationDelay: "60ms" }}>{t('sidebar_section_explore')}</p>
          <NavItem icon={ShoppingBag} label={t('sidebar_shop')} desc={t('sidebar_shop_desc')} tint="bg-blue-500/15 text-blue-300" active={isActive("/shop")} onClick={() => go("/shop")} delay={80} testId="sidebar-shop" />

          <div className="sb-item" style={{ animationDelay: "110ms" }}>
            <NavItem
              icon={Tag}
              label={t('sidebar_pricing')}
              desc={t('sidebar_pricing_desc')}
              tint="bg-emerald-500/15 text-emerald-300"
              onClick={() => handleProtectedAction(() => setExpandedTarif((v) => !v))}
              delay={0}
              testId="sidebar-pricing"
              trailing={<ChevronDown className={`h-4 w-4 shrink-0 text-white/30 transition-transform duration-300 ${expandedTarif ? "rotate-180" : ""}`} />}
            />
            <div className={`sb-collapse ${expandedTarif ? "open" : ""}`}><div>
              <div className="ml-[22px] space-y-0.5 border-l border-white/[0.07] py-1 pl-3">
                {shopBots.map(bot => (
                  <div key={bot.id}>
                    <button onClick={() => setOpenBot(openBot === bot.id ? null : bot.id)} className="flex w-full items-center justify-between rounded-lg px-2.5 py-2 text-[13px] transition-colors hover:bg-white/[0.04]">
                      <span className="flex min-w-0 items-center gap-2">
                        {bot.downloadable ? <Package className="h-3.5 w-3.5 shrink-0" style={{ color: bot.color }} /> : <Bot className="h-3.5 w-3.5 shrink-0" style={{ color: bot.color }} />}
                        <span className="truncate font-medium text-white/80">{bot.name}</span>
                      </span>
                      <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-white/25 transition-transform duration-300 ${openBot === bot.id ? "rotate-180" : ""}`} />
                    </button>
                    <div className={`sb-collapse ${openBot === bot.id ? "open" : ""}`}><div>
                      <div className="space-y-0.5 pb-1.5 pl-2">
                        {(bot.plans || []).map(p => (
                          <button
                            key={p.id || p.name}
                            onClick={() => handleProtectedAction(() => go(`/checkout?bot=${bot.id}`))}
                            className="flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-xs transition-colors hover:bg-white/[0.05]"
                          >
                            <span className="text-white/55">{p.name}</span>
                            <span className="font-mono font-medium" style={{ color: bot.color }}>{p.price}€ <span className="font-sans font-normal text-white/25">/ {p.duration_label}</span></span>
                          </button>
                        ))}
                      </div>
                    </div></div>
                  </div>
                ))}
              </div>
            </div></div>
          </div>

          <NavItem icon={Columns3} label={t('sidebar_compare')} desc={t('sidebar_compare_desc')} tint="bg-violet-500/15 text-violet-300" active={isActive("/comparer")} onClick={() => go("/comparer")} delay={140} testId="sidebar-compare" />
          <NavItem icon={Sparkles} label="Vanta AI" desc={t('sidebar_ai_desc')} tint="bg-fuchsia-500/15 text-fuchsia-300" active={location.pathname.startsWith("/ai")} onClick={() => go("/ai")} delay={155} testId="sidebar-ai" />

          <p className="sb-item px-2.5 pb-1.5 pt-4 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-white/30" style={{ animationDelay: "170ms" }}>{t('sidebar_section_help')}</p>
          <NavItem
            icon={DiscordIcon}
            label="Discord"
            desc={t('sidebar_discord_desc')}
            tint="bg-[#5865F2]/20 text-[#8b93ff]"
            onClick={() => handleProtectedAction(() => { window.open("https://discord.gg/kuN6TGcgMk", "_blank"); onClose(); })}
            delay={190}
            testId="sidebar-discord"
            trailing={<ArrowUpRight className="h-4 w-4 shrink-0 text-white/20 transition group-hover:text-white/50" />}
          />
          <NavItem icon={Headphones} label={t('sidebar_support')} desc={t('sidebar_support_desc')} tint="bg-cyan-500/15 text-cyan-300" active={isActive("/support")} onClick={() => handleProtectedAction(() => go("/support"))} delay={220} testId="sidebar-support" />
          <NavItem
            icon={Activity}
            label={t('sidebar_status')}
            desc={t('sidebar_status_desc')}
            tint="bg-amber-500/15 text-amber-300"
            active={isActive("/status")}
            onClick={() => go("/status")}
            delay={250}
            testId="sidebar-status"
            trailing={status ? (
              <span className={`flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-[10.5px] font-medium ${status.bg} ${status.text}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${status.dot} sb-live`} />{status.label}
              </span>
            ) : null}
          />
        </nav>

        {/* Moyens de paiement */}
        <div className="sb-item relative border-t border-white/[0.06] px-4 pb-4 pt-3.5" style={{ animationDelay: "300ms" }}>
          <p className="mb-2.5 text-center text-[10px] uppercase tracking-[0.14em] text-white/25">{t('sidebar_payment_methods')}</p>
          <div className="overflow-hidden whitespace-nowrap [mask-image:linear-gradient(90deg,transparent,#000_12%,#000_88%,transparent)]">
            <div className="sb-marquee inline-flex">
              {[...PAYMENT_LOGOS, ...PAYMENT_LOGOS].map((p, i) => (
                <div key={i} className="mr-3 shrink-0 opacity-50 transition-opacity hover:opacity-90">
                  {p.svg}
                </div>
              ))}
            </div>
          </div>
          <div className="mt-3 flex items-center justify-center gap-3 text-[10.5px] text-white/25">
            <button onClick={() => go("/mentions-legales")} className="hover:text-white/60">Mentions légales</button>
            <span>·</span>
            <button onClick={() => go("/cgv")} className="hover:text-white/60">CGV</button>
            <span>·</span>
            <button onClick={() => go("/politique-confidentialite")} className="hover:text-white/60">Confidentialité</button>
          </div>
        </div>
      </aside>
    </>
  );
}
